import { useEffect } from 'react';
import { useBrandingStore } from '../store';
import { adminDomainAPI } from '../services/api';

/**
 * The branding of the admin address this page is on (backend
 * GET /api/admin-domains/branding): on a tenant's (branch's) own subdomain its
 * name and logo, and the browser tab's title and icon too; everywhere else
 * HotWiFi's. Asked once per page load, by the sign-in page and the panel only —
 * the captive portal keeps its own.
 *
 *   const { own, name, logoUrl, kind } = useAdminBranding();
 *   own — true on a tenant's or branch's address; kind 'superadmin' on super.hotwifi.in
 */
const DEFAULT_TITLE = typeof document !== 'undefined' ? document.title : '';
let asked = null;

function applyToDocument(branding, own) {
    if (own) document.title = `${branding.name} — WiFi Management`;
    else if (branding.kind === 'superadmin') document.title = 'HotWiFi — Super Admin';
    else document.title = DEFAULT_TITLE;

    const link = document.querySelector('link[rel="icon"]');
    if (!link) return;
    if (!link.dataset.defaultHref) link.dataset.defaultHref = link.getAttribute('href') || '';
    const icon = own ? (branding.faviconUrl || branding.logoUrl) : null;
    link.setAttribute('href', icon || link.dataset.defaultHref);
}

export default function useAdminBranding() {
    const branding = useBrandingStore((s) => s.branding);
    const setBranding = useBrandingStore((s) => s.setBranding);
    const own = (branding.kind === 'tenant' || branding.kind === 'branch') && !!branding.name;

    useEffect(() => {
        if (!asked) {
            // Unanswered, the address keeps what it showed last time.
            asked = Promise.resolve()
                .then(() => adminDomainAPI.branding())
                .then((res) => res?.data?.data || null)
                .catch(() => null);
        }
        let live = true;
        asked.then((fresh) => { if (live && fresh) setBranding(fresh); });
        return () => { live = false; };
    }, [setBranding]);

    useEffect(() => { applyToDocument(branding, own); }, [branding, own]);

    return { ...branding, own, name: own ? branding.name : 'HotWiFi' };
}
